import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { formatDateToDDMMYYYY } from "./const";
import { toast } from "react-hot-toast";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { createErpSocket } from "../utils/socketIo";
import { listPurchases } from "../erpClient";
import {
  normalizeListPurchasesRows,
  isPurchasesApiSuccess,
  purchaseSocketPayloadToTableRow,
} from "../utils/purchasesErpAdapter";
import { parseApiPagination } from "../utils/parseApiPagination";

function PurchaseTable() {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [purchases, setPurchases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const fetchPurchases = useCallback(
    async (page = 1, limit = 10, search = "") => {
      setIsLoading(true);
      try {
        const response = await listPurchases({
          search,
          page,
          limit,
        });

        if (!isPurchasesApiSuccess(response)) {
          setPurchases([]);
          setTotalCount(0);
          return;
        }

        // `normalizeListPurchasesRows` intentionally returns legacy keys:
        // `purchase_no`, `bill_date`, `karigar_*`, `items_count`, `total_nwt`, `total_amount`.
        // It currently does NOT pass through `total_fine_wt`, so we read fine wt from raw API row.
        const listRaw = normalizeListPurchasesRows(response);

        const list = (Array.isArray(listRaw) ? listRaw : []).map((p) => ({
          id: p.id,
          purchase_no: p.purchase_no ?? "",
          bill_date: p.bill_date ?? "",
          supplier_company_name: p.karigar_company_name ?? "",
          supplier_name: p.karigar_name ?? "",
          items_count: p.items_count ?? 0,
          total_gwt: p.total_gwt ?? p.total_nwt ?? "",
          total_fine_wt: p.total_fine_wt ?? "",
        }));
        list.sort((a, b) => Number(b.id) - Number(a.id));
        setPurchases(list);
        setTotalCount(parseApiPagination(response, list.length).total);
      } catch (error) {
        console.error("Error fetching purchases:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch purchase data",
        );
        setPurchases([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchPurchases(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );

    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchPurchases]);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in PurchaseTable, refetching purchases...",
        event.detail,
      );
      setCurrentPage(1);
      fetchPurchases(1, itemsPerPage, searchQuery);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, fetchPurchases]);

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("PurchaseTable: Connected to socket server");
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("PurchaseTable: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("PurchaseTable: Socket connection error:", error);
    });

    // Listen for new purchase creation (legacy purchase_details or newERP purchase)
    newSocket.on("purchase_created", (data) => {
      const newPurchase = purchaseSocketPayloadToTableRow(data);
      if (!newPurchase?.id) return;

      setPurchases((prev) => {
        // Check if purchase already exists (avoid duplicates)
        const exists = prev.some((p) => p.id === newPurchase.id);
        if (exists) {
          return prev.map((p) => (p.id === newPurchase.id ? newPurchase : p));
        }
        // Add new purchase at the beginning (newest first)
        const updated = [newPurchase, ...prev].sort(
          (a, b) => Number(b.id) - Number(a.id),
        );
        setTotalCount((c) => c + 1);
        return updated;
      });
    });

    newSocket.on("purchase_updated", (data) => {
      const updatedPurchase = purchaseSocketPayloadToTableRow(data);
      if (!updatedPurchase?.id) return;

      setPurchases((prev) =>
        prev.map((p) =>
          String(p.id) === String(updatedPurchase.id) ? updatedPurchase : p,
        ),
      );
    });

    return () => {
      newSocket.off("purchase_created");
      newSocket.off("purchase_updated");
      newSocket.disconnect();
    };
  }, []);

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Purchase Table</h4>
            <div className="d-flex gap-2">
              <button
                className={`btn btn-secondary ${buttonClass}`}
                onClick={() => navigate("/purchaseledger")}
              >
                Ledger
              </button>
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={() => navigate("/addpurchase")}
              >
                Add Purchase
              </button>
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={() => navigate("/purchaseratecut")}
              >
                Rate Cut
              </button>
            </div>
          </div>

          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search by purchase no / name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Purchase No</th>
                    <th>Bill Date</th>
                    <th>Supplier</th>
                    <th>Items</th>
                    <th>G.Wt</th>
                    <th>Fine Wt</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : purchases.length > 0 ? (
                    purchases.map((p) => {
                      return (
                        <tr key={p.id}>
                          <td>{p.purchase_no}</td>
                          <td>{formatDateToDDMMYYYY(p.bill_date)}</td>
                          <td>
                            <div>
                              {p.supplier_company_name && (
                                <div>{p.supplier_company_name}</div>
                              )}
                              {p.supplier_name && (
                                <div
                                  style={{
                                    fontSize: "0.85rem",
                                    color: "#6c757d",
                                  }}
                                >
                                  {p.supplier_name}
                                </div>
                              )}
                              {!p.supplier_company_name && !p.supplier_name && (
                                <span>N/A</span>
                              )}
                            </div>
                          </td>
                          <td>{p.items_count}</td>
                          <td>{p.total_gwt || "-"}</td>
                          <td>
                            {p.total_fine_wt ||
                              p.total_finewt ||
                              p.total_fwt ||
                              p.total_fine ||
                              "-"}
                          </td>
                          <td>
                            <button
                              className={`btn btn-primary btn-sm ${buttonClass}`}
                              onClick={() =>
                                navigate("/editpurchase", {
                                  state: { purchaseId: p.id },
                                })
                              }
                            >
                              <Icon
                                icon="solar:pen-bold"
                                width="16"
                                height="16"
                              />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No purchases found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchaseTable;
